import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Form, Table, Typography } from 'antd';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import {
  ColSize,
  InputCurrencyForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../shared/components';

import {
  CarAccessoryTable,
  StepTwoFormContext,
} from '../../../../contexts/StepTwoForm';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../../../shared/utils';

import { COLORS } from '../../../../../../../../../../../config/themes/colors';
import { WizardDataFormContext } from '../../../../../../Wizard/contexts/WizardDataForm';
import { QuotationInfoContext } from '../../../../../../../shared';

interface AccessoriesProps {
  duplicateId?: number;
  id: number;
}

interface TableAccessoryItem {
  accessoryDescription: string;
  key: string;
  price: string;
}

export const Accessories: FC<AccessoriesProps> = ({ duplicateId, id }) => {
  const validateForm = new ValidateForm();

  const { stepTwoForm } = useContext(WizardDataFormContext);
  const { quotationToRenewal } = useContext(QuotationInfoContext);
  const {
    accessoriesList,
    accessoryTable,
    isLoadingAccessoriesList,
    setAccessoryTable,
  } = useContext(StepTwoFormContext);

  const [accessoryList, setAccessoryList] = useState(accessoriesList);
  const isRenewal = useMemo(() => Boolean(quotationToRenewal), [quotationToRenewal]);

  const handleDuplicatePlan = useCallback(() => {
    if (duplicateId && accessoryTable && !accessoryTable[id]) {
      const tableDuplicate = accessoryTable[duplicateId];

      if (tableDuplicate) {
        const duplicatedAccessories = tableDuplicate.map(
          (item: TableAccessoryItem) => ({
            ...item,
            key: String(Math.random()),
          })
        );

        setAccessoryTable((prevTable: any) => {
          const updatedTable = { ...prevTable };

          if (!updatedTable[id]) updatedTable[id] = [];

          updatedTable[id] = [...updatedTable[id], ...duplicatedAccessories];

          return updatedTable;
        });
      }
    }
  }, [duplicateId, accessoryTable, id, setAccessoryTable]);

  const filterAccessoriesList = useCallback(() => {
    if (accessoryTable) {
      const filteredAccessories = accessoriesList?.filter(
        (item) =>
          !accessoryTable[id]?.some(
            (accessory: any) => item.label === accessory.accessoryDescription
          )
      );

      setAccessoryList(filteredAccessories);
    }
  }, [accessoryTable, accessoriesList, setAccessoryList, id]);

  const onAddNewAccessory = useCallback(async () => {
    try {
      await stepTwoForm?.validateFields([
        `accessory-tab-${id}`,
        `accessory-price-tab-${id}`,
      ]);

      const accessory = stepTwoForm?.getFieldsValue();

      const accessoryDescription = accessory[`accessory-tab-${id}`];

      const price = accessory[`accessory-price-tab-${id}`];

      const newAccessory: TableAccessoryItem = {
        key: String(Math.random()),
        accessoryDescription,
        price,
      };

      setAccessoryTable((prevTable: any) => {
        const updatedTable = { ...prevTable };

        if (!updatedTable[id]) updatedTable[id] = [];

        updatedTable[id] = [...updatedTable[id], newAccessory];

        return updatedTable;
      });

      stepTwoForm?.resetFields([`accessory-tab-${id}`]);
      stepTwoForm?.resetFields([`accessory-price-tab-${id}`]);
    } catch (error) {}
  }, [stepTwoForm, id, setAccessoryTable]);

  const onDeleteAccessory = useCallback(
    (key: string) => {
      const filteredAccessories = accessoryTable[id].filter(
        (element: CarAccessoryTable) => element.key !== key
      );

      setAccessoryTable((prevTable: any) => {
        const updatedTable = { ...prevTable };

        if (!updatedTable[id]) updatedTable[id] = [];

        updatedTable[id] = filteredAccessories;

        return updatedTable;
      });
    },
    [accessoryTable, setAccessoryTable, id]
  );

  const MOCK_TABLE_COLUMNS_HEADER = useMemo(
    () => [
      {
        title: 'Accesorio',
        dataIndex: 'accessoryDescription',
      },
      {
        title: 'Precio',
        dataIndex: 'price',
        render: (text: string) => <span>{`$${formattedPrice(text)}`}</span>,
      },
      {
        title: 'Acción',
        dataIndex: 'action',
        className: 'action-column',
        width: '10%',
        render: (_: any, data: TableAccessoryItem) => (
          <ActionButton
            label=''
            icon={<DeleteOutlined />}
            color='danger'
            type='secondary'
            onClick={isRenewal ? undefined : () => onDeleteAccessory(data.key)}
            disabled={isRenewal}
          />
        ),
      },
    ],
    [onDeleteAccessory, isRenewal]
  );

  useEffect(() => {
    handleDuplicatePlan();
  }, [handleDuplicatePlan]);

  useEffect(() => {
    filterAccessoriesList();
  }, [filterAccessoriesList]);

  return (
    <>
      <RowSize>
        <Typography.Title
          level={3}
          style={{
            margin: 0,
            marginBottom: 10,
            color: COLORS.secondary[100],
          }}
        >
          Accesorios
        </Typography.Title>
      </RowSize>

      <RowSize>
        <ColSize size={25}>
          <SelectForm
            formProps={{
              label: 'Accesorio:',
              name: `accessory-tab-${id}`,
              rules: validateForm.generateRules({
                label: 'Accesorio',
                rules: ['required'],
              }),
            }}
            selectProps={{
              disabled: isRenewal || accessoryList.length === 0 || isLoadingAccessoriesList,
              loading: isLoadingAccessoriesList,
              options: accessoryList,
              showSearch: true,
            }}
          />
        </ColSize>

        <ColSize size={25}>
          <InputCurrencyForm
            formProps={{
              label: 'Precio:',
              name: `accessory-price-tab-${id}`,
              rules: validateForm.generateRules({
                label: 'precio',
                rules: ['required'],
              }),
            }}
            inputCurrencyProps={{ disabled: isRenewal }}
          />
        </ColSize>

        <ColSize size={25}>
          <Form.Item label=' '>
            <ActionButton
              className='btn-accent-outline-barbuss'
              label='Agregar accesorio'
              onClick={isRenewal ? undefined : onAddNewAccessory}
              icon={<PlusOutlined />}
              type='secondary'
              disabled={isRenewal}
            />
          </Form.Item>
        </ColSize>

        <ColSize size={100}>
          <Table
            bordered
            className='person-table mb-1'
            columns={MOCK_TABLE_COLUMNS_HEADER}
            dataSource={accessoryTable[id]}
            pagination={false}
            scroll={{ x: 'auto' }}
          />
        </ColSize>
      </RowSize>
    </>
  );
};
